import { useTranslation } from 'react-i18next'
import React, { useMemo } from 'react'
import { Button } from 'react-bootstrap'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import EnsConsignmentItemTableRow from './EnsConsignmentItemTableRow'
import { useFetchConsignmentItems } from '../../../../consignment-item/api/fetchConsignmentItems'
import useCreateConsignmentItem from '../../../../consignment-item/components/useCreateConsignmentItem'
import useDeleteConsignmentItem from '../../../../consignment-item/components/useDeleteConsignmentItem'
import useDuplicateConsignmentItem from '../../../../consignment-item/components/useDuplicateConsignmentItem'
import usePatchConsignmentItem from '../../../../consignment-item/components/usePatchConsignmentItem'
import { ConsignmentItemPatch } from '../../../../consignment-item/models/request'
import useToastError from '../../../../../../shared/components/toast-error/useToastError'
import { useConfirm } from '../../../../../../shared/components/confirm/ConfirmProvider'
import LoadingOverlay from '../../../../../../components/p5/LoadingOverlay'
import useDeclarationItemsTableActions from '../../../components/useDeclarationItemsTableActions'
import useBulkDeleteConsignmentItems from '../../../../consignment-item/components/useBulkDeleteConsignmentItems'
import ConsignmentItemsTableActions from '../../../../consignment-item/components/ConsignmentItemsTableActions'
import Checkbox from '../../../../../../components/p5/Checkbox'
import { useDeclarationEditorContext } from '../../../contexts/DeclarationEditorContext'

interface EnsConsignmentItemTableProps {
  consignmentId: number
  houseConsignmentId: number
  onOpenConsignmentItem: (consignmentItemId: number, consignmentItemIndex: number) => void
  editDisabled: boolean
  isConsignmentItemDrawerOpen: boolean
}

function EnsConsignmentItemTable({
  consignmentId,
  houseConsignmentId,
  onOpenConsignmentItem,
  editDisabled,
  isConsignmentItemDrawerOpen,
} : EnsConsignmentItemTableProps) {
  const { t } = useTranslation()
  const { confirm } = useConfirm()

  const { editorData } = useDeclarationEditorContext()
  const departureCustomsOffice = editorData?.departureCustomsOffice
  const country = useMemo(
    () => departureCustomsOffice?.slice('DEPARTURE_OFFICE_'.length, 'DEPARTURE_OFFICE_'.length + 2) ?? null,
    [departureCustomsOffice],
  )

  const { data, isLoading } = useFetchConsignmentItems({ consignmentId, houseConsignmentId })
  const consignmentItems = useMemo(() => data ?? [], [data])
  const { saveConsignmentItemDraft } = useCreateConsignmentItem(consignmentId, houseConsignmentId)
  const { deleteConsignmentItem } = useDeleteConsignmentItem(consignmentId, houseConsignmentId)
  const { duplicate } = useDuplicateConsignmentItem(consignmentId, houseConsignmentId)
  const { patchConsignmentItem } = usePatchConsignmentItem(consignmentId, houseConsignmentId)
  const { bulkDelete, isBulkDeleting } = useBulkDeleteConsignmentItems(consignmentId, houseConsignmentId)

  const { notifyApiError } = useToastError()

  const rowIds = useMemo(() => consignmentItems.map((row) => row.id), [consignmentItems])

  const {
    selectedRowIds,
    selectedCount,
    allSelected,
    someSelected,
    deleteSelected,
    isBulkDeleteNotAllowed,
    clearSelection,
    toggleAllRowsSelected,
    toggleRowSelect,
  } = useDeclarationItemsTableActions({
    rowIds,
    isBulkDeleting,
    isInlineEditActive: isConsignmentItemDrawerOpen,
    hasUnsavedChanges: false,
    onDeleteSelected: async (ids: number[]) => {
      const ok = await confirm({
        message: t('table.deleteConsignmentItemConfirmation', { count: ids.length }),
      })
      if (!ok) {
        return
      }

      try {
        await bulkDelete(Array.from(ids))
        toast.success(t('table.deletedConsignmentItems', { count: ids.length }))
      } catch (e) {
        reportApiFailure(e, () => notifyApiError(e))
      }
    },
  })

  const addRow = async () => {
    try {
      const savedConsignmentItem = await saveConsignmentItemDraft({
        sequenceNumber: consignmentItems.length,
      })

      if (savedConsignmentItem == null) {
        toast.error(t('consignmentItem.create', { context: 'failed' }))
        return
      }
      toast.success(t('consignmentItem.create', { context: 'success' }))
    } catch (err) {
      reportApiFailure(err, () => toast.error(t('consignmentItem.create', { context: 'failed' })))
    }
  }

  const removeRow = async (id: number) => {
    const ok = await confirm({
      message: t('table.deleteConsignmentItemConfirmation', { count: 1 }),
    })
    if (!ok) {
      return
    }

    try {
      await deleteConsignmentItem(id)
      toast.success(t('table.deletedConsignmentItems', { count: 1 }))
    } catch (err) {
      reportApiFailure(err, () => notifyApiError(err))
    }
  }

  const handleRowDuplicate = async (id: number) => {
    try {
      await duplicate(id)
      toast.success(t('consignmentItem.duplicate', { context: 'success' }))
    } catch (err) {
      reportApiFailure(err, () => toast.error(t('consignmentItem.duplicate', { context: 'failed' })))
    }
  }

  const saveRow = async (id: number, patch: ConsignmentItemPatch) => {
    try {
      await patchConsignmentItem(id, patch)
      toast.success(t('consignmentItem.saveSuccess'))
    } catch (err) {
      reportApiFailure(err, () => toast.error(t('consignmentItem.saveError')))
      throw err
    }
  }

  const isAddButtonDisabled = consignmentItems.length > 999 || editDisabled
  const isRowEditDisabled = editDisabled || selectedCount > 0

  return (
    <LoadingOverlay loading={isLoading} minHeight="0">
      {selectedCount > 0 && (
        <div className="mb-3">
          <ConsignmentItemsTableActions
            selectedCount={selectedCount}
            rows={consignmentItems}
            onClearSelection={clearSelection}
            onDeleteSelected={deleteSelected}
            isBulkDeleteNotAllowed={isBulkDeleteNotAllowed}
            isBulkDeleting={isBulkDeleting}
          />
        </div>
      )}
      <div className="row">
        <div className="col-12">
          <div className="table-responsive">
            <table className="consignment-table">
              <thead>
                <tr>
                  <th className="w-1 whitespace-nowrap align-middle">
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && someSelected}
                      onChange={toggleAllRowsSelected}
                      wrapperClassName="d-flex align-items-center justify-content-center"
                      inputClassName="form-check-input m-0 float-none"
                    />
                  </th>
                  <th className="sequence-nr">{t('declaration.p5.sequenceNumber')}</th>
                  <th>{t('declaration.p5.descriptionOfGoods')}</th>
                  <th>{t('declaration.p5.commodityCode')}</th>
                  <th className="goods-col-measure">{t('declaration.p5.quantity')}</th>
                  <th className="goods-col-measure">{t('declaration.p5.grossMass')}</th>
                  <th className="col-1">&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {consignmentItems.map((rowData, index) => (
                  <EnsConsignmentItemTableRow
                    key={rowData.id}
                    rowData={rowData}
                    rowIndex={index}
                    country={country}
                    onRowEdit={() => onOpenConsignmentItem(rowData.id, index)}
                    onRowSave={(patch) => saveRow(rowData.id, patch)}
                    onRowRemove={() => removeRow(rowData.id)}
                    onRowDuplicate={() => handleRowDuplicate(rowData.id)}
                    onRowSelect={() => toggleRowSelect(rowData.id)}
                    selected={selectedRowIds.has(rowData.id)}
                    editDisabled={isRowEditDisabled}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12 text-end pt-3 pb-3">
          <Button
            className="btn btn-sm btn-primary"
            onClick={() => addRow()}
            disabled={isAddButtonDisabled}
          >
            <i className="fa fa-plus me-2" />
            {t('declaration.p5.addGoodsItem')}
          </Button>
        </div>
      </div>
    </LoadingOverlay>
  )
}

export default EnsConsignmentItemTable
